import { GetPlaceTopWaters_entry_edges_node } from '../../../interfaces/graphql';
import { Heading } from '../../Heading/Heading';
import { Text } from '../../Text/Text';
import { getNodesFromConnection } from '../../../helpers/graphql';
import { FishCatchesIcon } from '../../Icons/FishCatchesIcon';
import get from 'ts-get';
import { ImageLoaderProps } from 'next/image';
import { getImgProxyLoader } from '../../../helpers/imageLoaders/imgProxy';
import { NextImageWrapper } from '../../NextImage/NextImageWrapper';
import { StarIcon } from '../../Icons/StarIcon';
import { twMerge } from 'tailwind-merge';
import { InternalLink } from '../../Clickables/InternalLink/InternalLink';
import { getFishingWaterUrl } from '../../../helpers/urls';

interface IProps {
  className?: string;
  fishingWater: GetPlaceTopWaters_entry_edges_node;
}

export const FishingWaterCard = ({ className, fishingWater }: IProps) => {
  const id = fishingWater._id;
  const name = fishingWater.displayName;
  const allReviews = fishingWater.allReviews;
  const catches = getNodesFromConnection(fishingWater.recentCatches);
  const displayName = fishingWater.displayName;
  const region = fishingWater.region;
  const country = fishingWater.country;
  const countryName = country?.countryName;
  const regionName = region?.name;
  const catchCount = catches.length;

  const wrapperClassName = (catchCount: number, index: number) => {
    switch (catchCount) {
      case 1:
        return 'row-span-2 col-span-2';
      case 2:
        return 'row-span-1 col-span-2';
      case 3: {
        return index === 0 ? 'row-span-1 col-span-2' : 'row-span-1 col-span-1';
      }
      default:
        return 'row-span-1 col-span-1';
    }
  };

  return (
    <InternalLink
      className={twMerge('bg-lake-fog relative flex flex-row rounded-lg md:rounded-xl', className)}
      to={getFishingWaterUrl(id, name)}
    >
      <div className="grid min-h-28 min-w-28 grid-cols-2 grid-rows-2 gap-px overflow-hidden rounded-l-lg md:min-h-40 md:min-w-40 md:rounded-l-xl">
        {catches.map(({ post }, index) => {
          const image = get(post, p => p.images.edges[0].node);
          const imageUrl = image?.url ?? '';
          const imageWidth = image?.width ?? 0;
          const imageHeight = image?.height;

          const imgProxyLoader = ({ src, width, quality }: ImageLoaderProps): string =>
            getImgProxyLoader(src, width, imageWidth, imageHeight, { quality });

          return image ? (
            <div key={post._id} className={`${wrapperClassName(catchCount, index)} relative`}>
              <NextImageWrapper
                wrapperClassName="size-full"
                className="object-cover"
                customPlaceholderIcon={<FishCatchesIcon size={50} />}
                alt="catch"
                src={imageUrl}
                fill
                loader={imgProxyLoader}
                sizes="(min-width: 600px) 148px, 65px"
              />
            </div>
          ) : (
            <FishCatchesIcon size={50} />
          );
        })}
      </div>

      <div className="flex grow flex-col gap-1 overflow-hidden p-3 sm:p-3 md:p-4">
        <Heading variant="2xs" as="span">
          {displayName}
        </Heading>
        <Text variant="xs" className="m-0">
          {regionName}, {countryName}
        </Text>
        {!!allReviews?.totalCount && allReviews.totalCount > 0 && (
          <div className="flex items-center gap-1">
            <Text variant="xs" as="span" className="text-clownfish mb-px">
              <StarIcon height={12} width={12} />
            </Text>
            <Text variant="xs" as="span" data-testid="ratingNumber">
              {allReviews.totalAverage.toFixed(1)}
            </Text>
          </div>
        )}
      </div>
    </InternalLink>
  );
};
